import { useEffect, useState, useCallback, useRef, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { PopupModal, useCalendlyEventListener } from 'react-calendly'
import api from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { BookOpen, ChevronRight, Users, ShoppingBag, Send } from 'lucide-react'
import { getContestCategoryContent } from '../data/cccdContestCategories'
import { getCourseCoverUrl, hasCourseCover } from '../lib/course-covers'
import { Button } from '@/components/ui/button'
import { PageHeader, PageSpinner, PageEmpty, Section, RuledList, ruledRowLink } from '@/components/page'
import { EmptyDashboard } from '@/components/academy/EmptyDashboard'
import { DiscoverMore } from '@/components/academy/DiscoverMore'
import { HomeHero } from '@/components/academy/HomeHero'
import { toastError } from '../lib/toast'
import { useCccdCatalog, submitSupplyRequest, type CatalogItemCategory } from '../hooks/useCccdCatalog'
import { MemberPhotoTile } from '../components/cccd/MemberPhotoTile'

// Acción primaria en forma de enlace (mismo aspecto que Button, que no renderiza <a>).
const primaryLink =
  'motion-press motion-link-arrow inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 text-sm font-bold text-primary-foreground transition-[filter,transform] hover:brightness-110 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50'

interface PendingMember {
  id: string
  name: string
  email: string
  reconciledUserId: string | null
  photoUrl: string | null
}

interface SeedbedCourseSummary {
  id: string
  title: string
  slug: string
  thumbnail_url: string | null
}

interface GroupTeamInfo {
  name: string
  eventId: string
  eventName: string
  scope: 'national' | 'regional'
  categorySlug: string | null
  groupPhotoUrl: string | null
}

const CATALOG_CATEGORY_LABELS: Record<CatalogItemCategory, string> = {
  structure: 'Estructuras',
  electronics: 'Electrónica',
  launch_equipment: 'Equip. de lanzamiento',
  merchandising: 'Merchandising',
}

// Catálogo de construcción/decoración habilitado para el evento del equipo
// (ver openspec/changes/cccd-team-supply-catalog) — sin cobro: el equipo solo
// arma una lista deseada y la envía como solicitud, que el admin revisa desde
// Gestión. Nunca muestra un total a pagar ni un botón de pago.
function SupplyCatalogSection() {
  const { items, isLoading, error } = useCccdCatalog(true)
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const selectedCount = Object.values(quantities).filter((q) => q > 0).length

  function setQuantity(itemId: string, quantity: number) {
    setSubmitted(false)
    setSubmitError(null)
    setQuantities((prev) => ({ ...prev, [itemId]: Math.min(99, Math.max(0, Math.floor(quantity))) }))
  }

  async function handleSubmit() {
    const selected = Object.entries(quantities)
      .filter(([, quantity]) => quantity > 0)
      .map(([catalogItemId, quantity]) => ({ catalogItemId, quantity }))
    if (selected.length === 0) return

    setSubmitting(true)
    setSubmitError(null)
    try {
      await submitSupplyRequest(selected)
      setQuantities({})
      setSubmitted(true)
    } catch (err: any) {
      // El botón vuelve a habilitarse; el error se puede reintentar sin perder la selección.
      setSubmitError(err?.response?.data?.error ?? 'No pudimos enviar tu solicitud. Revisa tu conexión e intenta de nuevo; tu selección se conserva.')
    } finally {
      setSubmitting(false)
    }
  }

  const grouped = items.reduce<Record<string, typeof items>>((acc, item) => {
    (acc[item.category] ??= []).push(item)
    return acc
  }, {})

  return (
    <Section title="Catálogo de construcción y decoración">
      {isLoading ? (
        <PageSpinner label="Cargando catálogo" />
      ) : error ? (
        <PageEmpty title="No pudimos cargar el catálogo" description={error} />
      ) : items.length === 0 ? (
        <PageEmpty title="Aún no hay artículos habilitados" description="Tu evento todavía no tiene artículos en el catálogo de construcción." />
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, categoryItems]) => (
            <div key={category}>
              <h3 className="mb-1 text-sm font-medium text-muted-foreground">
                {CATALOG_CATEGORY_LABELS[category as CatalogItemCategory] ?? category}
              </h3>
              <RuledList>
                {categoryItems.map((item) => (
                  <li key={item.id} className="grid grid-cols-[72px_1fr] items-center gap-x-4 gap-y-2 py-4 sm:grid-cols-[96px_1fr_auto]">
                    {item.imageUrl ? (
                      <div className="aspect-video overflow-hidden rounded-md bg-muted">
                        <img src={item.imageUrl} alt={item.name} className="h-full w-full object-cover" />
                      </div>
                    ) : (
                      <div className="flex aspect-video items-center justify-center rounded-md bg-muted">
                        <ShoppingBag className="h-5 w-5 text-muted-foreground/60" aria-hidden="true" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground">{item.name}</p>
                      {item.description && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{item.description}</p>}
                      <p className="mt-1 text-sm font-semibold tabular-nums text-foreground">
                        ${item.priceCop.toLocaleString('es-CO')} COP
                        <span className="ml-1.5 text-xs font-normal text-muted-foreground">Referencial, sin cobro</span>
                      </p>
                    </div>
                    <div className="col-span-2 flex items-center gap-2 sm:col-span-1">
                      <label htmlFor={`qty-${item.id}`} className="text-xs text-muted-foreground">Cantidad</label>
                      <input
                        id={`qty-${item.id}`}
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={99}
                        value={quantities[item.id] ?? 0}
                        onChange={(e) => setQuantity(item.id, Number(e.target.value) || 0)}
                        className="h-11 w-20 rounded-md border border-border bg-background px-2 text-sm tabular-nums focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 sm:h-8 sm:w-16"
                      />
                    </div>
                  </li>
                ))}
              </RuledList>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={handleSubmit} disabled={selectedCount === 0 || submitting}>
              <Send className="mr-1.5 h-4 w-4" />
              {submitting ? 'Enviando…' : 'Enviar solicitud'}
            </Button>
            <div role="status" aria-live="polite">
              {submitted && (
                <p className="text-sm text-primary">Solicitud enviada. El equipo de ALAS Corp la revisará y se pondrá en contacto.</p>
              )}
              {submitError && <p className="text-sm text-destructive">{submitError}</p>}
            </div>
          </div>
        </div>
      )}
    </Section>
  )
}

function GroupDashboard() {
  const { group } = useAuthStore()
  const [members, setMembers] = useState<PendingMember[]>([])
  const [courses, setCourses] = useState<SeedbedCourseSummary[]>([])
  const [team, setTeam] = useState<GroupTeamInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const [membersRes, meRes] = await Promise.all([
        api.get('/academy/seedbeds/group-session/members'),
        api.get('/academy/seedbeds/me'),
      ])
      setMembers(membersRes.data.data ?? [])
      setCourses(meRes.data.data?.courses ?? [])
      setTeam(meRes.data.data?.team ?? null)
    } catch (err) {
      console.error(err)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  if (loading) {
    return <PageSpinner label="Cargando tu equipo" />
  }

  if (error) {
    return (
      <PageEmpty
        title="No pudimos cargar el dashboard"
        description="Hubo un problema de conexión. Intenta de nuevo en unos segundos."
        action={<Button onClick={load}>Reintentar</Button>}
      />
    )
  }

  return (
    <div className="space-y-10">
      {/* Foto de portada del equipo — superficie navy de respaldo cuando aún no hay foto real */}
      <div className="relative h-48 overflow-hidden rounded-xl sm:h-64 lg:h-72">
        {team?.groupPhotoUrl ? (
          <img src={team.groupPhotoUrl} alt={`Foto del equipo ${group?.name}`} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-sidebar">
            <Users className="h-16 w-16 text-white/10" aria-hidden="true" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        {team && (
          <div className="absolute left-4 top-4 flex flex-wrap gap-2 sm:left-6 sm:top-5">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5">
              <Users className="h-3 w-3 text-white" aria-hidden="true" />
              <span className="text-micro font-bold uppercase tracking-wide text-white">
                CCCD {team.scope === 'national' ? 'Nacional' : 'Regional'} · {team.eventName}
              </span>
            </div>
            {team.categorySlug && (
              <div className="inline-flex items-center rounded-full bg-primary px-3 py-1.5">
                <span className="text-micro font-bold uppercase tracking-wide text-white">
                  {getContestCategoryContent(team.categorySlug)?.title ?? team.categorySlug}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="absolute bottom-4 left-4 right-4 sm:bottom-5 sm:left-6">
          <PageHeader variant="cover" title={group?.name ?? ''} />
        </div>
      </div>

      <Section title="Cursos de este semillero">
        {courses.length === 0 ? (
          <PageEmpty title="Este semillero aún no tiene cursos asignados" />
        ) : (
          <RuledList>
            {courses.map((course) => (
              <li key={course.id}>
                <Link
                  to={`/courses/${course.slug}`}
                  className={`${ruledRowLink} flex min-h-11 items-center gap-4 py-3.5`}
                >
                  {hasCourseCover(course) ? (
                    <div className="aspect-video w-28 shrink-0 overflow-hidden rounded-md">
                      <img src={getCourseCoverUrl(course)} alt="" className="h-full w-full object-cover" />
                    </div>
                  ) : (
                    <div className="flex aspect-video w-28 shrink-0 items-center justify-center rounded-md bg-muted">
                      <BookOpen className="h-5 w-5 text-muted-foreground/60" aria-hidden="true" />
                    </div>
                  )}
                  <p className="min-w-0 flex-1 text-sm font-semibold text-foreground">{course.title}</p>
                  <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground transition-colors group-hover:text-foreground">
                    Ver curso <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                </Link>
              </li>
            ))}
          </RuledList>
        )}
      </Section>

      <Section title="Integrantes del equipo">
        {members.length === 0 ? (
          <PageEmpty
            title="Aún no hay integrantes registrados"
            description="Pídele al administrador que los agregue desde el panel de gestión."
          />
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4">
            {members.map((m) => (
              <div key={m.id}>
                <div className="overflow-hidden rounded-lg">
                  <MemberPhotoTile name={m.name} photoUrl={m.photoUrl} />
                </div>
                <p className="mt-2 line-clamp-2 text-sm font-semibold text-foreground" title={m.name}>{m.name}</p>
                <p className="truncate text-xs text-muted-foreground">{m.email}</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      <ExpertMeetingsSection />

      <SupplyCatalogSection />
    </div>
  )
}

interface ExpertBooking {
  id: string
  createdAt: string
}

interface CategoryExpert {
  categoryExpertId: string
  name: string
  bio: string | null
  avatarUrl: string | null
  schedulingUrl: string
  maxMeetings: number | null
  used: number
  remaining: number | null
  bookings: ExpertBooking[]
}

// cccd-expert-meetings-confirm-redesign: la reunión NO ocurre en la
// plataforma — Calendly la agenda (Google Calendar + Meet vía su
// integración nativa) EMBEBIDA en un modal, nunca en pestaña nueva. El cupo
// se consume solo cuando el navegador recibe el evento onEventScheduled de
// Calendly (POST .../confirm) — abrir el modal o cerrarlo sin completar la
// reserva no afecta el cupo. Esto funciona igual para todo experto, tenga o
// no plan pago de Calendly (ver design.md).
function ExpertMeetingsSection() {
  const [experts, setExperts] = useState<CategoryExpert[]>([])
  const [loading, setLoading] = useState(true)
  const [activeExpert, setActiveExpert] = useState<CategoryExpert | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [releasingId, setReleasingId] = useState<string | null>(null)
  // useCalendlyEventListener no recibe cuál experto está activo — se lee
  // desde este ref (no desde el state) para que el handler nunca vea un
  // valor obsoleto si el listener quedó registrado en un render anterior.
  const activeExpertRef = useRef<CategoryExpert | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/cccd/me/experts')
      setExperts(res.data.data?.experts ?? [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  function openMeetingModal(expert: CategoryExpert) {
    activeExpertRef.current = expert
    setActiveExpert(expert)
  }

  function closeMeetingModal() {
    activeExpertRef.current = null
    setActiveExpert(null)
  }

  useCalendlyEventListener({
    onEventScheduled: async (e) => {
      const expert = activeExpertRef.current
      const calendlyEventUri = e.data.payload?.event?.uri
      if (!expert || !calendlyEventUri) return
      setConfirming(true)
      try {
        await api.post(`/cccd/me/experts/${expert.categoryExpertId}/confirm`, { calendlyEventUri })
        await load()
      } catch (err: any) {
        toastError(err?.response?.data?.error ?? 'La reunión se agendó en Calendly, pero no pudimos confirmar el cupo. Contacta a soporte.')
      } finally {
        setConfirming(false)
        closeMeetingModal()
      }
    },
  })

  async function releaseBooking(booking: ExpertBooking) {
    setReleasingId(booking.id)
    try {
      await api.post(`/cccd/me/bookings/${booking.id}/release`)
      await load()
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo liberar el cupo. Intenta de nuevo.')
    } finally {
      setReleasingId(null)
    }
  }

  if (loading) return null

  return (
    <Section title="Agenda una reunión con un experto">
      {experts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Todavía no hay un experto asignado a la categoría de tu equipo. Vuelve pronto.
        </p>
      ) : (
      <RuledList>
        {experts.map((expert) => {
          const noQuota = expert.remaining === 0
          return (
            <li key={expert.categoryExpertId} className="flex flex-col gap-4 py-5 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 gap-4">
                {expert.avatarUrl ? (
                  <img src={expert.avatarUrl} alt={expert.name} className="h-12 w-12 shrink-0 rounded-full object-cover" />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold text-muted-foreground">
                    {expert.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                )}
                <div className="min-w-0 space-y-1">
                  <p className="font-semibold text-foreground">{expert.name}</p>
                  {expert.maxMeetings != null && (
                    <p className="text-xs tabular-nums text-muted-foreground">{expert.used} de {expert.maxMeetings} reuniones usadas</p>
                  )}
                  {expert.bio && <p className="line-clamp-3 max-w-prose text-sm text-muted-foreground">{expert.bio}</p>}
                  {expert.bookings.length > 0 && (
                    <ul className="space-y-1 pt-1 text-xs text-muted-foreground">
                      {expert.bookings.map((booking) => (
                        <li key={booking.id} className="flex flex-wrap items-center gap-x-3">
                          <span>Reunión agendada el {new Date(booking.createdAt).toLocaleDateString('es-CO')}</span>
                          <button
                            type="button"
                            onClick={() => releaseBooking(booking)}
                            disabled={releasingId === booking.id}
                            className="shrink-0 font-semibold text-primary underline-offset-2 hover:underline disabled:opacity-50"
                          >
                            {releasingId === booking.id ? 'Liberando…' : 'Cancelé, liberar mi cupo'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <Button
                onClick={() => openMeetingModal(expert)}
                disabled={noQuota}
                className="shrink-0"
              >
                {noQuota ? 'Sin cupo disponible' : 'Agendar reunión'}
              </Button>
            </li>
          )
        })}
      </RuledList>
      )}
      {activeExpert && (
        <PopupModal
          url={activeExpert.schedulingUrl}
          onModalClose={closeMeetingModal}
          open={!!activeExpert}
          rootElement={document.getElementById('root')!}
        />
      )}
      {confirming && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
          <div className="rounded-lg bg-card px-6 py-4 text-sm font-medium text-foreground shadow-xl" role="status" aria-live="polite">
            Confirmando tu reunión…
          </div>
        </div>
      )}
    </Section>
  )
}

interface LastLesson {
  id: string
  title: string
  last_position_seconds: number
  last_accessed_at: string
}

interface EnrolledCourse {
  id: string
  title: string
  slug: string
  thumbnail_url: string
  category: string
  difficulty_level: string
  enrollment_status: string
  enrolled_at: string
  total_lessons: number
  completed_lessons: number
  last_lesson?: LastLesson | null
}

function lessonToSlug(title: string) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

// El padre solo ramifica: cada dashboard tiene sus propios hooks, así que un
// cambio de sessionType nunca altera el orden de hooks de un mismo componente.
export default function Dashboard() {
  const sessionType = useAuthStore((s) => s.sessionType)
  return sessionType === 'group' ? <GroupDashboard /> : <StudentDashboard />
}

function StudentDashboard() {
  const user = useAuthStore((s) => s.user)
  const [courses, setCourses] = useState<EnrolledCourse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const firstName = user?.name?.split(' ')[0]
  const greeting = firstName ? `Hola, ${firstName}` : 'Hola'

  const loadDashboard = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const coursesRes = await api.get('/academy/courses/enrolled/me')
      setCourses(coursesRes.data.data || [])
    } catch (err) {
      console.error(err)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadDashboard() }, [loadDashboard])

  // enrollment_status ya no es equivalente a "100% de lecciones": completar
  // el curso también exige aprobar todo examen obligatorio (SectionQuiz.isRequired)
  // — ver openspec/changes/academy-required-exams-block-completion.
  const completedCourses = courses.filter((c) => c.enrollment_status === 'completed')
  const activeCourses = courses.filter((c) => c.enrollment_status !== 'completed')

  if (loading) {
    return <PageSpinner label="Cargando tu dashboard" />
  }

  if (error) {
    return (
      <PageEmpty
        title="No pudimos cargar tu dashboard"
        description="Hubo un problema de conexión. Intenta de nuevo en unos segundos."
        action={<Button onClick={loadDashboard}>Reintentar</Button>}
      />
    )
  }

  // Recencia: cualquier curso activo con una lección vista, ordenado por último acceso.
  const recentCourses = activeCourses
    .filter(c => c.last_lesson && c.last_lesson.last_accessed_at)
    .sort((a, b) =>
      new Date(b.last_lesson!.last_accessed_at).getTime() -
      new Date(a.last_lesson!.last_accessed_at).getTime()
    )

  // Única acción de "continuar": el curso visto más recientemente; si nunca vio
  // nada, el primero activo. La franja "Tus cursos" usa el mismo orden.
  const resumeCourse = recentCourses[0] ?? activeCourses[0]
  const stripCourses = [
    ...recentCourses,
    ...activeCourses.filter(c => !recentCourses.includes(c)),
  ].slice(0, 8)
  const resumeUrl = resumeCourse
    ? resumeCourse.last_lesson
      ? `/courses/${resumeCourse.slug}/${lessonToSlug(resumeCourse.last_lesson.title)}`
      : `/courses/${resumeCourse.slug}`
    : '/mis-cursos'

  if (courses.length === 0) {
    return <EmptyDashboard greeting={greeting} />
  }

  const remainingLessons = resumeCourse
    ? Math.max(0, resumeCourse.total_lessons - resumeCourse.completed_lessons)
    : 0
  const resumeProg = resumeCourse && resumeCourse.total_lessons > 0
    ? Math.round((resumeCourse.completed_lessons / resumeCourse.total_lessons) * 100)
    : 0

  return (
    <div className="space-y-12 selection:bg-primary/30">
      {/* CONTINUAR: hero a ancho completo con la portada del curso; el avance es una línea fina en su borde inferior */}
      <HomeHero
        greeting={greeting}
        subtitle={`${completedCourses.length} de ${courses.length} ${courses.length === 1 ? 'curso completado' : 'cursos completados'}`}
        cover={resumeCourse ? getCourseCoverUrl(resumeCourse) : undefined}
        heading={resumeCourse ? resumeCourse.title : 'Completaste todos tus cursos'}
        // "Continuar" reabre la última lección vista (no hay dato de lección siguiente en este endpoint).
        text={
          resumeCourse
            ? resumeCourse.last_lesson
              ? <>Retoma en <span className="font-medium text-foreground">{resumeCourse.last_lesson.title}</span>.{remainingLessons > 0 && ` ${remainingLessons === 1 ? 'Te falta 1 clase' : `Te faltan ${remainingLessons} clases`} para terminar.`}</>
              : 'Empieza por la primera lección.'
            : 'Revisa tus cursos y sigue aprendiendo.'
        }
        progress={resumeCourse ? { value: resumeProg, label: `Progreso del curso ${resumeCourse.title}` } : undefined}
      >
        {resumeCourse && (
          <Link to={resumeUrl} className={primaryLink}>
            Continuar
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        )}
        <Link
          to="/mis-cursos"
          className={resumeCourse
            ? 'motion-press inline-flex min-h-11 items-center rounded-lg px-4 text-sm font-semibold text-foreground/80 transition-[background-color,color,transform] hover:bg-foreground/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50'
            : primaryLink}
        >
          Ver mis cursos
        </Link>
      </HomeHero>

      <div>
        <Section
          titleId="tus-cursos"
          title="Tus cursos"
          action={
            <Link
              to="/mis-cursos"
              className="inline-flex min-h-11 items-center gap-1 rounded-sm text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 sm:min-h-0"
            >
              Ver todos
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          }
        >
          {stripCourses.length === 0 ? (
            <PageEmpty
              title="No tienes cursos en progreso"
              description={`Has completado ${completedCourses.length} ${completedCourses.length === 1 ? 'curso' : 'cursos'}. Revisa tus cursos y sigue aprendiendo.`}
              action={
                <Link to="/mis-cursos" className="inline-flex min-h-11 items-center rounded-lg border border-border px-4 text-sm font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
                  Ver mis cursos
                </Link>
              }
            />
          ) : (
            <ul className="grid gap-x-5 gap-y-8 grid-cols-[repeat(auto-fit,minmax(17rem,1fr))] [&>*]:max-w-[40rem]">
              {stripCourses.map((course, i) => {
                const prog = course.total_lessons > 0
                  ? Math.round((course.completed_lessons / course.total_lessons) * 100)
                  : 0
                const courseUrl = course.last_lesson
                  ? `/courses/${course.slug}/${lessonToSlug(course.last_lesson.title)}`
                  : `/courses/${course.slug}`
                return (
                  <li key={course.id} style={{ '--i': i } as CSSProperties} className="motion-stagger-item">
                    <Link
                      to={courseUrl}
                      className="group flex flex-col gap-3 rounded-2xl outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-foreground/5 ring-1 ring-foreground/10">
                        <img
                          src={getCourseCoverUrl(course)}
                          alt=""
                          className="h-full w-full object-cover transition-transform duration-(--dur-slow) ease-(--ease-out-expo) group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                        />
                        {prog > 0 && (
                          <div
                            role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={prog}
                            aria-label={`Progreso del curso ${course.title}`}
                            className="absolute inset-x-0 bottom-0 h-1 bg-black/40"
                          >
                            <div
                              className="motion-progress motion-progress-in h-full w-full bg-primary"
                              style={{ transform: `scaleX(${prog / 100})` }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 px-1">
                        <p className="line-clamp-2 text-base font-semibold leading-snug tracking-tight text-foreground">{course.title}</p>
                        <p className="mt-1 truncate text-sm text-muted-foreground">
                          {course.last_lesson ? course.last_lesson.title : 'Aún no empiezas este curso'}
                        </p>
                      </div>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </Section>
      </div>

      <DiscoverMore excludeIds={courses.map((c) => c.id)} />
    </div>
  )
}
